import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Alert,
    Snackbar,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Switch,
    FormControlLabel,
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    DragIndicator as DragIcon,
    Help as HelpIcon,
} from "@mui/icons-material";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
    getServices,
    createService,
    updateService,
    deleteService,
    updateServiceSortOrder,
    getServiceSettings,
    updateServiceSettings,
} from "../../../api/api";
import { getCurrentBranchId } from "../../../utils/auth";
import { planLimit } from "../../../utils/plan";
import OnboardingTutorialOverlay from "../../../components/OnboardingTutorialOverlay";

// Sortable Table Row Component
const SortableServiceRow = ({ service, onEdit, onDelete }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: service.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        cursor: isDragging ? 'grabbing' : 'grab',
    };

    return (
        <TableRow ref={setNodeRef} style={style}>
            <TableCell sx={{ width: 50, px: 1 }}>
                <Box
                    {...attributes}
                    {...listeners}
                    sx={{
                        cursor: 'grab',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                        '&:hover': {
                            color: 'primary.main',
                        },
                    }}
                >
                    <DragIcon />
                </Box>
            </TableCell>
            <TableCell>{service.service_name}</TableCell>
            <TableCell>
                <Typography
                    variant="body2"
                    sx={{
                        fontFamily: "monospace",
                        fontWeight: "bold",
                        color: "primary.main",
                    }}
                >
                    {service.service_tag}
                </Typography>
            </TableCell>
            <TableCell align="right">
                <IconButton
                    onClick={() => onEdit(service)}
                    color="primary"
                    size="small"
                >
                    <EditIcon />
                </IconButton>
                <IconButton
                    onClick={() => onDelete(service.id)}
                    color="error"
                    size="small"
                >
                    <DeleteIcon />
                </IconButton>
            </TableCell>
        </TableRow>
    );
};

const ServiceManagement = () => {
    const branchId = getCurrentBranchId();
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

    const showToast = (message, severity = "success") => {
        setToast({ open: true, message, severity });
    };
    const [openDialog, setOpenDialog] = useState(false);
    const [editingService, setEditingService] = useState(null);
    const [formData, setFormData] = useState({
        service_name: "",
        service_tag: "",
    });
    const [serviceSelectionEnabled, setServiceSelectionEnabled] = useState(true);
    const [smsEnabled, setSmsEnabled] = useState(false);
    const [serviceSelectionText, setServiceSelectionText] = useState("");
    const [savingServiceSelectionText, setSavingServiceSelectionText] = useState(false);
    const [selectedServicesLabel, setSelectedServicesLabel] = useState("");
    const [savingSelectedServicesLabel, setSavingSelectedServicesLabel] = useState(false);
    const [settingsLoading, setSettingsLoading] = useState(true);
    const [runTutorial, setRunTutorial] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        fetchServices();
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await getServiceSettings(branchId);
            if (response.success) {
                setServiceSelectionEnabled(response.data.service_selection_enabled);
                setSmsEnabled(response.data.sms_enabled ?? false);
                setServiceSelectionText(response.data.service_selection_text || "");
                setSelectedServicesLabel(response.data.selected_services_label || "");
            }
        } catch (err) {
            console.error("Error fetching settings:", err);
        } finally {
            setSettingsLoading(false);
        }
    };

    const handleSaveServiceSelectionText = async () => {
        try {
            setSavingServiceSelectionText(true);
            await updateServiceSettings(branchId, { service_selection_text: serviceSelectionText });
            showToast("Service selection text updated successfully");
        } catch (err) {
            console.error("Error saving service selection text:", err);
            showToast("Failed to update service selection text", "error");
        } finally {
            setSavingServiceSelectionText(false);
        }
    };

    const handleSaveSelectedServicesLabel = async () => {
        try {
            setSavingSelectedServicesLabel(true);
            await updateServiceSettings(branchId, { selected_services_label: selectedServicesLabel });
            showToast("Selected services text updated successfully");
        } catch (err) {
            console.error("Error saving selected services text:", err);
            showToast("Failed to update selected services text", "error");
        } finally {
            setSavingSelectedServicesLabel(false);
        }
    };

    const fetchServices = async () => {
        try {
            setLoading(true);
            const response = await getServices(branchId);
            if (response.success) {
                setServices(response.data);
            } else {
                showToast("Failed to fetch services", "error");
            }
        } catch (err) {
            showToast("Error loading services", "error");
            console.error("Error fetching services:", err);
        } finally {
            setLoading(false);
        }
    };

    // Plan-based service cap (null = unlimited).
    const maxServices = planLimit("max_services");
    const serviceLimitReached =
        maxServices !== null && maxServices !== undefined && services.length >= maxServices;

    const handleCreateService = () => {
        if (serviceLimitReached) {
            showToast(
                `Your current plan allows up to ${maxServices} services. Upgrade your plan to add more.`,
                "warning"
            );
            return;
        }
        setEditingService(null);
        setFormData({ service_name: "", service_tag: "" });
        setOpenDialog(true);
    };

    const handleEditService = (service) => {
        setEditingService(service);
        setFormData({
            service_name: service.service_name,
            service_tag: service.service_tag,
        });
        setOpenDialog(true);
    };

    const handleDeleteService = async (id) => {
        if (window.confirm("Are you sure you want to delete this service?")) {
            try {
                const response = await deleteService(id);
                if (response.success) {
                    setServices(services.filter((service) => service.id !== id));
                    showToast("Service deleted successfully");
                } else {
                    showToast("Failed to delete service", "error");
                }
            } catch (err) {
                showToast("Error deleting service", "error");
                console.error("Error deleting service:", err);
            }
        }
    };

    const handleDialogClose = () => {
        setOpenDialog(false);
        setEditingService(null);
        setFormData({ service_name: "", service_tag: "" });
    };

    const handleSave = async () => {
        try {
            const data = {
                ...formData,
                branch_id: branchId,
            };

            console.log('Saving service:', data);

            let response;
            if (editingService) {
                response = await updateService(editingService.id, formData);
            } else {
                response = await createService(data);
            }

            console.log('Service save response:', response);

            if (response.success) {
                await fetchServices();
                showToast(editingService ? "Service updated successfully" : "Service created successfully");
                handleDialogClose();
            } else {
                const errorMsg = response.error || response.errors || "Failed to save service";
                showToast(errorMsg, "error");
            }
        } catch (err) {
            console.error("Error saving service:", err);
            const errorMsg = err.error || err.response?.data?.error || err.message || "Error saving service";
            showToast(errorMsg, "error");
        }
    };

    const handleToggleServiceSelection = async (event) => {
        const newValue = event.target.checked;
        setServiceSelectionEnabled(newValue);
        try {
            await updateServiceSettings(branchId, { service_selection_enabled: newValue });
            showToast("Settings updated successfully");
        } catch (err) {
            console.error("Error updating settings:", err);
            showToast("Failed to update settings", "error");
            setServiceSelectionEnabled(!newValue); // Revert
        }
    };

    const handleToggleSms = async (event) => {
        const newValue = event.target.checked;
        setSmsEnabled(newValue);
        try {
            await updateServiceSettings(branchId, { sms_enabled: newValue });
            showToast("Settings updated successfully");
        } catch (err) {
            console.error("Error updating SMS setting:", err);
            showToast("Failed to update settings", "error");
            setSmsEnabled(!newValue); // Revert
        }
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
            return;
        }

        const oldIndex = services.findIndex((service) => service.id === active.id);
        const newIndex = services.findIndex((service) => service.id === over.id);

        if (oldIndex === -1 || newIndex === -1) {
            return;
        }

        const newItems = arrayMove(services, oldIndex, newIndex);
        setServices(newItems);

        // Update sort order in backend
        try {
            const servicesWithNewOrder = newItems.map((service, index) => ({
                id: parseInt(service.id, 10),
                service_sort_order: parseInt(index, 10),
            }));

            console.log('Updating sort order:', servicesWithNewOrder);
            const response = await updateServiceSortOrder(servicesWithNewOrder);

            if (response.success) {
                showToast("Service order updated successfully");
            } else {
                const errorMsg = response.error || response.errors || "Failed to update service order";
                showToast(errorMsg, "error");
                // Revert the change
                setServices(services);
            }
        } catch (err) {
            console.error("Error updating sort order:", err);
            const errorMsg = err.error || err.response?.data?.error || err.message || "Failed to update service order";
            showToast(errorMsg, "error");
            // Revert the change
            setServices(services);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Tutorial overlay (driver.js) – same UX as /home/panel */}
            <OnboardingTutorialOverlay
                page="service_management"
                run={runTutorial}
                onPhaseComplete={() => setRunTutorial(false)}
                onClose={() => setRunTutorial(false)}
            />

            <Box>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="h4" component="h1">
                            Service Management
                        </Typography>
                        <IconButton
                            onClick={() => setRunTutorial(true)}
                            title="Show Tutorial"
                            color="primary"
                        >
                            <HelpIcon />
                        </IconButton>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleCreateService}
                        disabled={serviceLimitReached}
                        title={
                            serviceLimitReached
                                ? `Your plan allows up to ${maxServices} services. Upgrade to add more.`
                                : undefined
                        }
                        data-onboarding-id="onboarding-services-add"
                    >
                        Add New Service
                    </Button>
                </Box>

                {settingsLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                <>
                <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={serviceSelectionEnabled}
                                onChange={handleToggleServiceSelection}
                                color="primary"
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Enable Service Selection Page
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    If disabled, users will skip the service selection page and be assigned a queue number directly.
                                </Typography>
                                <Typography variant="body2" color="info.main" sx={{ mt: 0.5 }}>
                                    Go to the Customer View in the menu to preview the display.
                                </Typography>
                            </Box>
                        }
                    />
                </Box>

                <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={smsEnabled}
                                onChange={handleToggleSms}
                                color="primary"
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Enable SMS Queue Registration
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Adds a phone number field to the QR page so customers can join the queue by SMS instead of scanning. Each registration sends a real text message.
                                </Typography>
                                <Typography variant="body2" color="info.main" sx={{ mt: 0.5 }}>
                                    Go to the QR Code page in the menu to preview the display.
                                </Typography>
                            </Box>
                        }
                    />
                </Box>

                </>
                )}

                <TableContainer component={Paper} sx={{ mb: 3 }}>
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: 50 }}></TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>Service Name</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>Service Tag</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: "bold" }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <SortableContext
                                    items={services.map((service) => service.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {services.map((service) => (
                                        <SortableServiceRow
                                            key={service.id}
                                            service={service}
                                            onEdit={handleEditService}
                                            onDelete={handleDeleteService}
                                        />
                                    ))}
                                </SortableContext>
                            </TableBody>
                        </Table>
                    </DndContext>
                </TableContainer>

                {services.length === 0 && (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography variant="h6" color="text.secondary">
                            No services found. Create your first service to get started!
                        </Typography>
                    </Box>
                )}

                {!settingsLoading && (
                <>
                <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                        Service Selection Text
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        This text is shown when customers choose services. Explain what they should select (for example, what kind of help they need).
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={serviceSelectionText}
                            onChange={(e) => setServiceSelectionText(e.target.value)}
                            placeholder="We only have one queue, but if we know what you need help with we can assist you better. You can choose multiple services."
                            variant="outlined"
                            size="small"
                            sx={{ flex: 1 }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleSaveServiceSelectionText}
                            disabled={savingServiceSelectionText}
                        >
                            {savingServiceSelectionText ? <CircularProgress size={24} /> : "Save"}
                        </Button>
                    </Box>
                </Box>

                <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                        Selected Services Text
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Label shown above the list of selected services (e.g., "Your selected services:").
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                        <TextField
                            fullWidth
                            value={selectedServicesLabel}
                            onChange={(e) => setSelectedServicesLabel(e.target.value)}
                            placeholder="Your selected services:"
                            variant="outlined"
                            size="small"
                            inputProps={{ maxLength: 255 }}
                            sx={{ flex: 1 }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleSaveSelectedServicesLabel}
                            disabled={savingSelectedServicesLabel}
                        >
                            {savingSelectedServicesLabel ? <CircularProgress size={24} /> : "Save"}
                        </Button>
                    </Box>
                </Box>
                </>
                )}

                {/* Add/Edit Dialog */}
                <Dialog
                    open={openDialog}
                    onClose={handleDialogClose}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        {editingService ? "Edit Service" : "Create New Service"}
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <TextField
                                fullWidth
                                label="Service Name"
                                value={formData.service_name}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        service_name: e.target.value,
                                    })
                                }
                                margin="normal"
                                required
                                inputProps={{ maxLength: 100 }}
                            />
                            <TextField
                                fullWidth
                                label="Service Tag"
                                value={formData.service_tag}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        service_tag: e.target.value,
                                    })
                                }
                                margin="normal"
                                required
                                helperText="Tag will be displayed on queue cards (e.g., CHECKOUT, ASSISTANCE, RETURN, etc.)"
                                inputProps={{ maxLength: 255 }}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDialogClose}>Cancel</Button>
                        <Button
                            onClick={handleSave}
                            variant="contained"
                            disabled={!formData.service_name || !formData.service_tag}
                        >
                            {editingService ? "Update" : "Create"}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>

            <Snackbar
                open={toast.open}
                autoHideDuration={3000}
                onClose={() => setToast((t) => ({ ...t, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setToast((t) => ({ ...t, open: false }))}
                    severity={toast.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {toast.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ServiceManagement;

